import React, { useEffect, useState } from "react";
import { ClipboardList, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/drill/AppHeader";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { recordAudit } from "@/lib/auditLog";
import { Button } from "@/components/ui/button";

export default function AdminRequestsPage() {
  const { effectiveRole, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      if (currentUser?.id) await base44.auth.syncNow?.(currentUser.id);
      const list = await base44.entities.AccountRecoveryRequest.list("-requested_at");
      setRequests(list || []);
    } catch (error) {
      toast({ title: "Failed to load requests", description: error.message || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (effectiveRole === "admin") load();
  }, [effectiveRole]);

  const markReviewed = async (request) => {
    setUpdating(request.id);
    try {
      await base44.entities.AccountRecoveryRequest.update(request.id, {
        status: "reviewed",
        reviewed_by: currentUser?.id || null,
        reviewed_at: new Date().toISOString(),
      });
      await recordAudit("account_recovery_reviewed", "AccountRecoveryRequest", request.id, {
        requester_email: request.requester_email || null,
      });
      await load();
    } catch (error) {
      toast({ title: "Failed to update request", description: error.message || "Please try again.", variant: "destructive" });
    } finally {
      setUpdating(null);
    }
  };

  if (effectiveRole !== "admin") {
    return <div className="min-h-screen flex items-center justify-center p-6"><p>Admin access required.</p></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Requests" subtitle="Account recovery and reset requests" backTo="/" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <ClipboardList className="h-4 w-4" />
            {requests.length} request{requests.length === 1 ? "" : "s"}
            {requests.filter((request) => String(request.status || "pending").toLowerCase() === "pending").length > 0 && (
              <span className="ml-2 inline-flex min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold items-center justify-center">
                {requests.filter((request) => String(request.status || "pending").toLowerCase() === "pending").length}
              </span>
            )}
          </div>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/admin/user-management")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> User management
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border bg-white p-6 text-sm text-slate-500 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400">
            No account recovery requests.
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <div key={request.id} className="rounded-2xl border bg-white p-5 dark:bg-slate-900 dark:border-slate-800">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{request.requester_email || "Unknown email"}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Requested: {request.requested_at ? new Date(request.requested_at).toLocaleString() : "—"}
                    </div>
                    <div className="text-sm mt-2">Status: <span className="font-medium">{request.status || "pending"}</span></div>
                    {request.reason && <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">Reason: {request.reason}</div>}
                  </div>
                  {request.status === "pending" && (
                    <Button className="rounded-xl" disabled={updating === request.id} onClick={() => markReviewed(request)}>
                      {updating === request.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Mark reviewed
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
